/**
 * ClosedAreaContainer — owns the collapse state, the per-group truncation
 * state, and the reopen-dialog flow for the board's Closed area (SE-5496;
 * SE-6862 added the truncation state).
 *
 * The area is collapsed by default (closed history should not dominate the
 * board). Once expanded, each outcome group still shows only its first card
 * until someone lifts that group's cap — which this container tracks per
 * outcome, so revealing the passed players does not also unfold every other
 * group. Choosing Reopen on a card opens the {@link ReopenCardDialog}; on
 * confirm it delegates to the injected `onReopen`, which the board hook wires to
 * `reopenKanbanCard`. Reopen targets are the board's WORKING (non-terminal)
 * stages only — a closed card can never be reopened straight into another
 * terminal stage.
 * @module features/player-kanban/components/organisms/ClosedArea/ClosedAreaContainer
 */

import { useCallback, useMemo, useState } from "react";

import ClosedAreaView from "./ClosedAreaView";
import ReopenCardDialog, {
  type ReopenStageOption,
} from "../../molecules/ReopenCardDialog";

import type { ClosedCardEntry, ClosedGroupData } from "../../../types/types";
import type { KanbanCardOutcome } from "@/generated/graphql";
import { closedRailLabel } from "../../../utils/closedRailLabel";

/** Props for {@link ClosedAreaContainer}. */
export interface ClosedAreaContainerProps {
  /** Closed groups, pre-ordered by `validOutcomes`. */
  readonly closedGroups: readonly ClosedGroupData[];
  /** Working (non-terminal) stages a card may be reopened into. */
  readonly workingStages: readonly ReopenStageOption[];
  /** Whether the viewer may reopen cards. */
  readonly canReopen: boolean;
  /**
   * Reopen a card into a working stage. Resolves on success; rejects on a real
   * error so the dialog can stay open.
   */
  readonly onReopen: (
    cardId: string,
    targetPhaseId: string,
    rationale?: string
  ) => Promise<void>;
}

/**
 * Container for the Closed area.
 * @param props - {@link ClosedAreaContainerProps}
 * @param props.closedGroups - Closed groups, pre-ordered
 * @param props.workingStages - Working stages for reopen
 * @param props.canReopen - Whether the viewer may reopen
 * @param props.onReopen - Reopen delegate
 * @returns The Closed area with its reopen dialog.
 */
const ClosedAreaContainer = ({
  closedGroups,
  workingStages,
  canReopen,
  onReopen,
}: ClosedAreaContainerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // SE-6862: which groups have had their one-card cap lifted. Expand-only —
  // nothing ever removes an outcome from this set, because re-hiding cards
  // someone deliberately revealed is worse than a long panel.
  const [showAllOutcomes, setShowAllOutcomes] = useState<
    ReadonlySet<KanbanCardOutcome>
  >(() => new Set());
  const [reopenEntry, setReopenEntry] = useState<ClosedCardEntry | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | undefined>(
    undefined
  );
  const [rationale, setRationale] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalCount = useMemo(
    () => closedGroups.reduce((sum, group) => sum + group.count, 0),
    [closedGroups]
  );

  const onToggle = useCallback(() => setIsExpanded(prev => !prev), []);

  // Pre-bind one handler per group so the view passes a plain reference
  // (react-perf/jsx-no-new-function-as-prop forbids inline closures). The
  // updater builds the next set by SPREADING rather than copy-then-add:
  // `.add()` is a mutation even on a fresh copy, which `functional/immutable-data`
  // rejects on sight.
  const showMoreHandlers = useMemo(
    () =>
      Object.fromEntries(
        closedGroups.map(group => [
          group.outcome,
          () => setShowAllOutcomes(prev => new Set([...prev, group.outcome])),
        ])
      ) as Partial<Record<KanbanCardOutcome, () => void>>,
    [closedGroups]
  );

  const onReopenPress = useCallback((entry: ClosedCardEntry) => {
    // Open the dialog fresh: never pre-fill a previous card's stage/reason.
    setReopenEntry(entry);
    setSelectedStageId(undefined);
    setRationale("");
  }, []);

  const onCancelReopen = useCallback(() => {
    setReopenEntry(null);
  }, []);

  const onSubmitReopen = useCallback(async () => {
    if (!reopenEntry || !selectedStageId) return;
    setIsSubmitting(true);
    try {
      await onReopen(reopenEntry.cardId, selectedStageId, rationale);
      // Success: close the dialog. The board reconciles via refetch upstream.
      setReopenEntry(null);
    } catch {
      // A real error was surfaced upstream (toast/setError); keep the dialog
      // open so the user can retry or cancel.
    } finally {
      setIsSubmitting(false);
    }
  }, [reopenEntry, selectedStageId, rationale, onReopen]);

  // Collapsed-rail summary is derived state, so the container owns it and
  // the View renders the resulting string (SE-8019).
  const railLabel = closedRailLabel(closedGroups);

  return (
    <>
      <ClosedAreaView
        closedGroups={closedGroups}
        railLabel={railLabel}
        totalCount={totalCount}
        isExpanded={isExpanded}
        onToggle={onToggle}
        canReopen={canReopen}
        onReopenPress={onReopenPress}
        showAllOutcomes={showAllOutcomes}
        showMoreHandlers={showMoreHandlers}
      />
      <ReopenCardDialog
        isOpen={reopenEntry !== null}
        playerName={reopenEntry?.player.name}
        workingStages={workingStages}
        selectedStageId={selectedStageId}
        onSelectStage={setSelectedStageId}
        rationale={rationale}
        onChangeRationale={setRationale}
        isSubmitting={isSubmitting}
        onSubmit={onSubmitReopen}
        onCancel={onCancelReopen}
      />
    </>
  );
};

ClosedAreaContainer.displayName = "ClosedAreaContainer";

export default ClosedAreaContainer;
